import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { liveUrl, token } from "./url";
import Cookies from "js-cookie";
import LoginComponent from "./LoginComponent";

export default function WhatsAppChat({ onClose }) {
  const params = useParams();
  const rawPropertyId = params.id || params.propertyId;
  const propertyId = String(rawPropertyId).replace("-", "");
  const userId = Cookies.get("userId") || "45";
  const [isLoggedIn, setIsLoggedIn] = useState(Cookies.get("isLoggedIn") === "true");
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isLoggedIn && propertyId && userId) {
      fetchMessages();
      pollingIntervalRef.current = setInterval(() => {
        fetchMessages();
      }, 50000);
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isLoggedIn, propertyId, userId]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${liveUrl}get-property-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userid: userId,
          property_id: propertyId,
        }),
      });
      if (!response.ok) throw new Error("Failed to fetch messages");
      const result = await response.json();
      if (result.status === "done" && result.data && Array.isArray(result.data)) {
        const allMessages = [];
        result.data.forEach((msg) => {
          if (msg.user_chat && msg.user_chat.trim() !== "") {
            allMessages.push({
              id: `user_${msg.id}`,
              dbId: msg.id,
              text: msg.user_chat.trim(),
              sender: "user",
              timestamp: msg.created_at || new Date().toISOString(),
            });
          }
          if (msg.admin_chat && msg.admin_chat.trim() !== "") {
            allMessages.push({
              id: `admin_${msg.id}`,
              dbId: msg.id,
              text: msg.admin_chat.trim(),
              sender: "admin",
              timestamp: msg.created_at || new Date().toISOString(),
            });
          }
        });
        allMessages.sort((a, b) => parseInt(a.dbId) - parseInt(b.dbId));
        setMessages(allMessages);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const saveMessageAPI = async (message) => {
    const response = await fetch(`${liveUrl}property-chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userid: userId,
        property_id: propertyId,
        user_chat: message,
      }),
    });
    if (!response.ok) throw new Error("Failed to save message");
    return await response.json();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    const userMessageText = inputMessage.trim();
    setInputMessage("");
    setIsLoading(true);
    try {
      await saveMessageAPI(userMessageText);
      await fetchMessages();
    } catch (error) {
      setInputMessage(userMessageText);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  const handleLoginSuccess = (userIdFromLogin) => {
    Cookies.set("isLoggedIn", "true", { expires: 7 });
    if (userIdFromLogin) {
      Cookies.set("userId", userIdFromLogin, { expires: 7 });
    }
    setIsLoggedIn(true);
  };

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {!isLoggedIn ? (
        <div className="p-4 sm:p-6">
          <LoginComponent onLoginSuccess={handleLoginSuccess} />
        </div>
      ) : (
        <>
          <div className="flex-1 p-6 overflow-y-auto bg-gray-50 min-h-[300px] max-h-[200px]">
            {messages.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
                <p className="text-sm font-medium text-gray-500 mb-2">No messages yet</p>
                <p className="text-xs text-gray-400">Start a conversation about this property</p>
              </div>
            )}
            {messages.map((msg) => (  
              <div key={msg.id} className={`mb-4 flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] sm:max-w-[70%] px-4 py-3 rounded-xl shadow-sm ${msg.sender === "user" ? "bg-blue-300 text-white rounded-br-sm" : "bg-green-300  text-gray-800 rounded-bl-sm border border-gray-200"}`}>
                  {msg.sender === "admin" && (
                    <div className="flex items-center mb-1">
                      <div className="w-5 h-5 !bg-blue-600 rounded-full flex items-center justify-center mr-1">
                        <span className="text-white text-xs font-bold">A</span>
                      </div>
                      <p className="text-xs !text-black font-semibold text-blue-600">Admin</p>
                    </div>
                  )}
                  <p className="text-sm !text-black break-words whitespace-pre-wrap">{msg.text}</p>
                  <p className={`text-xs mt-1 !text-black flex items-center ${msg.sender === "user" ? "text-blue-100 justify-end" : "text-gray-500"}`}>
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-end mb-4">
                <div className="bg-blue-100 rounded-xl px-4 py-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                    </div>
                    <span className="text-xs text-blue-600">Sending...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-white border-t border-gray-200">
            <form onSubmit={handleSendMessage} className="flex items-center space-x-2 sm:space-x-3">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 border border-gray-800 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={isLoading}
                autoFocus
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center space-x-1 sm:space-x-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
